import * as React from 'react';
import { ProjectMemberType } from 'types/clients';

import { useTheme, styled } from '@mui/material/styles';
import Popper from '@mui/material/Popper';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Autocomplete, { AutocompleteCloseReason, autocompleteClasses } from '@mui/material/Autocomplete';
import InputBase from '@mui/material/InputBase';
import { Box, Avatar, Typography, IconButton } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { Button } from 'ui-component/extended/Button';
import { FormattedMessage, useIntl } from 'react-intl';
import SearchIcon from '@mui/icons-material/Search';
import { get } from 'lodash';

interface PopperComponentProps {
    anchorEl?: any;
    disablePortal?: boolean;
    open: boolean;
}
const StyledAutocompletePopper = styled('div')(({ theme }) => ({
    [`& .${autocompleteClasses.paper}`]: {
        boxShadow: 'none',
        margin: 0,
        color: 'inherit',
        fontSize: 13
    },
    [`& .${autocompleteClasses.listbox}`]: {
        backgroundColor: theme.palette.mode === 'light' ? '#fff' : '#1c2128',
        padding: 0,
        [`& .${autocompleteClasses.option}`]: {
            minHeight: 'auto',
            alignItems: 'flex-start',
            padding: 16,
            borderTop: `1px solid ${theme.palette.grey[500]}`,
            '&[aria-selected="true"]': {
                backgroundColor: 'transparent'
            },
            [`&.${autocompleteClasses.focused}, &.${autocompleteClasses.focused}[aria-selected="true"]`]: {
                backgroundColor: theme.palette.action.hover
            }
        }
    },
    [`&.${autocompleteClasses.popperDisablePortal}`]: {
        position: 'relative'
    }
}));

function PopperComponent(props: PopperComponentProps) {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { disablePortal, anchorEl, open, ...other } = props;
    return <StyledAutocompletePopper {...other} />;
}

const StyledPopper = styled(Popper)(({ theme }) => ({
    border: `1px solid ${theme.palette.grey[500]} !important`,
    borderRadius: 8,
    margin: '4px 0 !important',
    width: '320px',
    zIndex: theme.zIndex.modal,
    fontSize: 13,
    color: theme.palette.mode === 'light' ? '#24292e' : '#c9d1d9',
    backgroundColor: `${theme.palette.background.paper} !important`,
    '.MuiAutocomplete-popper': {
        width: '318px !important',
        borderRadius: '0px !important',
        boxShadow: 'none !important'
    },
    '.MuiAutocomplete-paper': {
        borderRadius: '0 0 8px 8px'
    }
}));

const StyledInput = styled(InputBase)(({ theme }) => ({
    padding: 10,
    width: 'calc(100% - 34px)',
    borderBottom: `none`,
    '& input': {
        borderRadius: 4,
        backgroundColor: theme.palette.background.paper,
        padding: 8,
        transition: theme.transitions.create(['border-color', 'box-shadow']),
        border: `none`,
        fontSize: 14,
        '&:focus': {
            boxShadow: `none`,
            borderColor: 'none'
        }
    }
}));

interface PopOverSelectorProps<OptionType> {
    options: OptionType[];
    optionLabelPath: string;
    optionIconPath: string;
    setSelectedOption?: (item: OptionType) => void;
    buttonType?: 'icon' | 'button';
    buttonText?: string;
    buttonOverride?: React.FunctionComponent;
    searchInputPlaceholder?: string;
}
export function PopOverSelector<OptionType>({
    options,
    optionLabelPath,
    optionIconPath,
    buttonType = 'icon',
    buttonText = '',
    buttonOverride,
    setSelectedOption,
    searchInputPlaceholder
}: PopOverSelectorProps<OptionType>) {
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const [searchResults, setSearchResults] = React.useState<OptionType[]>(options);
    const intl = useIntl();
    const theme = useTheme();

    const handleOptionSelection = (valueAutocomplete: OptionType) => {
        setSelectedOption?.(valueAutocomplete);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };
    const handlePopOverOpening = (event: any) => {
        event.stopPropagation();
        setSearchResults(options);
        setAnchorEl(event.currentTarget);
    };
    const open = Boolean(anchorEl);
    const id = open ? 'github-label' : undefined;
    const handleClickOption = (option: any) => {
        handleOptionSelection(option);
    };

    return (
        <Box>
            <Box>
                {buttonOverride ? (
                    <Box onClick={handlePopOverOpening}>{buttonText}</Box>
                ) : (
                    <Box>
                        {buttonType === 'button' ? (
                            <Box sx={{ width: { xs: '100%', sm: '220px' } }}>
                                <Button
                                    title={<FormattedMessage id="add_provided_service" />}
                                    icon={<AddIcon />}
                                    onClick={handlePopOverOpening}
                                />
                            </Box>
                        ) : (
                            <IconButton onClick={handlePopOverOpening}>
                                <AddIcon sx={{ color: theme.palette.grey[400] }} />
                            </IconButton>
                        )}
                    </Box>
                )}
            </Box>

            <StyledPopper id={id} open={open} anchorEl={anchorEl} placement="bottom-start">
                <ClickAwayListener onClickAway={handleClose}>
                    <Box sx={{ zIndex: 1010 }}>
                        <Autocomplete
                            sx={{ width: '320px' }}
                            open
                            multiple
                            onClose={(_event: React.ChangeEvent<{}>, reason: AutocompleteCloseReason) => {
                                if (reason === 'escape') {
                                    handleClose();
                                }
                            }}
                            value={searchResults}
                            onChange={(event, newValue) => {
                                setSearchResults(newValue);
                                handleClose();
                            }}
                            PopperComponent={PopperComponent}
                            renderTags={() => null}
                            noOptionsText="No labels"
                            renderOption={(props, option: OptionType) => (
                                <li {...props}>
                                    <Box
                                        key={get(option, optionIconPath)}
                                        onClick={() => handleClickOption(option)}
                                        sx={{ display: 'flex', alignItems: 'center', width: '100%' }}
                                    >
                                        {/* {(option.icon || option.members) && ( */}
                                        <Box
                                            sx={{
                                                width: 32,
                                                height: 32,
                                                flexShrink: 0,
                                                borderRadius: '3px',
                                                mr: 1,
                                                mt: '2px',
                                                ml: '10px'
                                            }}
                                        >
                                            <Avatar
                                                sx={{ width: '32px', height: '32px' }}
                                                alt="Remy Sharp"
                                                src={get(option, optionIconPath)}
                                            />
                                        </Box>
                                        {/* )} */}
                                        <Box
                                            sx={{
                                                flexGrow: 1,
                                                '& span': {
                                                    color: theme.palette.grey[200]
                                                }
                                            }}
                                        >
                                            <Typography
                                                sx={{
                                                    color: theme.palette.grey[400],
                                                    fontFamily: 'Inter',
                                                    fontWeight: 500,
                                                    ml: buttonType === 'button' ? '8px' : '0px',
                                                    mt: buttonType === 'button' ? '0px' : '10px'
                                                }}
                                            >
                                                {get(option, optionLabelPath)}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </li>
                            )}
                            options={[...options]}
                            getOptionLabel={(option) => get(option, optionLabelPath)}
                            renderInput={(params) => (
                                <Box sx={{ display: 'flex', width: '320px', alignItem: 'center' }}>
                                    <SearchIcon
                                        sx={{
                                            m: '15px 0 0 20px',
                                            fill: theme.palette.grey[200]
                                        }}
                                    />
                                    <StyledInput
                                        ref={params.InputProps.ref}
                                        inputProps={params.inputProps}
                                        autoFocus
                                        placeholder={intl.formatMessage({ id: `${searchInputPlaceholder}` })}
                                    />
                                </Box>
                            )}
                        />
                    </Box>
                </ClickAwayListener>
            </StyledPopper>
        </Box>
    );
}
